import React from 'react';
import { T } from './helpers';
import ConsoleSkeleton from 'src/components/pages/console/consoleSkeleton';
import EarningsSkeleton from 'src/components/pages/earnings/earningsSkeleton';
import ReportAISkeleton from 'src/components/pages/reportAI/reportAISkeleton';

const Console = React.lazy(() => import(/* webpackChunkName: "console" */ 'src/components/pages/console'));
const Earnings = React.lazy(() => import(/* webpackChunkName: "earnings" */ 'src/components/pages/earnings'));
const ReportAI = React.lazy(() => import(/* webpackChunkName: "reportAI" */ 'src/components/pages/reportAI'));

const PlanManager = React.lazy(() => import(/* webpackChunkName: "planManager" */ 'src/components/pages/planManager'));
const MarketplaceThemes = React.lazy(() => import(/* webpackChunkName: "marketplaceThemes" */ 'src/components/pages/marketplaceThemes'));

export interface AdministratorRouteHeader {
    title: React.ReactNode;
    description?: React.ReactNode;
    backUrl?: string;
}

export interface AdministratorRoute {
    url: string;
    component: React.ComponentType<{}> | React.LazyExoticComponent<React.ComponentType<{}>>;
    skeleton?: React.ComponentType<{}>;
    header: AdministratorRouteHeader | ((params: Record<string, string | undefined>) => AdministratorRouteHeader);
}

const planManagerSubpageHeader = (params: Record<string, string | undefined>): AdministratorRouteHeader => {
    switch (params.subpage) {
        case 'custom-plan':
            return {
                title: T._('Create Custom Plan', 'Admin'),
                description: T._('Set the parameters to create a Custom Plan', 'Admin'),
                backUrl: '/plan-manager'
            };
        case 'seller-plan-builder':
            return {
                title: T._('Custom & Enterprise Plan Builder', 'Admin'),
                description: T._('Create unpublished V2 catalog plans for a seller from published V2 plans', 'Admin'),
                backUrl: '/plan-manager'
            };
        case 'extend-trial':
            return {
                title: T._('Extend Free Trial', 'Admin'),
                description: T._('Extend a free trial on Shopify', 'Admin'),
                backUrl: '/plan-manager'
            };
        case 'customer-overview':
            return {
                title: T._('Customer overview', 'Admin'),
                description: T._('Inspect the complete local subscription context for a seller', 'Admin'),
                backUrl: '/plan-manager'
            };
        case 'shopify-charge':
            return {
                title: T._('Shopify Charge', 'Admin'),
                description: T._('Create a Shopify charge for a seller.', 'Admin'),
                backUrl: '/plan-manager'
            };
        default:
            return {
                title: T._('Subscription Utilities', 'Admin'),
                description: T._('Manage plans, free trials, Shopify charges and customer subscription insights.', 'Admin'),
                backUrl: '/plan-manager'
            };
    }
};

const administratorRoutes: AdministratorRoute[] = [
    {
        url: '/report-ai',
        component: ReportAI,
        skeleton: ReportAISkeleton,
        header: {
            title: T._('Report AI', 'Admin'),
            description: T._('Monitor AI usage and free credits.', 'Admin'),
            backUrl: '/console'
        }
    },
    {
        url: '/console',
        component: Console,
        skeleton: ConsoleSkeleton,
        header: {
            title: T._('Console', 'Admin'),
            description: T._('Access administration tools and reports.', 'Admin')
        }
    },
    {
        url: '/earnings',
        component: Earnings,
        skeleton: EarningsSkeleton,
        header: {
            title: T._('Earnings', 'Admin'),
            description: T._('Monitor earnings by platform.', 'Admin'),
            backUrl: '/console'
        }
    },
    {
        url: '/plan-manager/:subpage',
        component: PlanManager,
        header: planManagerSubpageHeader
    },
    {
        url: '/plan-manager',
        component: PlanManager,
        header: {
            title: T._('Subscription Utilities', 'Admin'),
            description: T._('Inspect customers and manage plans, subscription trials and Shopify charges.', 'Admin')
        }
    },
    {
        url: '/marketplace-themes',
        component: MarketplaceThemes,
        header: {
            title: T._('Marketplace Themes', 'Admin'),
            description: T._('Create, edit, publish, and archive marketplace themes.', 'Admin')
        }
    }
];

export default administratorRoutes;
